import React, { useState } from "react";
import { motion } from "framer-motion";
import { Package, Search, CheckCircle2, Circle, MessageCircle, Loader2, Gift, Clock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useGet } from "@/lib/hooks";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function TrackOrder() {
  const { data: content } = useGet("/content");
  const wa = String(content?.whatsapp_number || "").replace(/\D/g, "");
  const [ref, setRef] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    if (!ref.trim() || !phone.trim()) return toast.error("Please enter your order reference and phone number.");
    setLoading(true); setNotFound(false); setResult(null);
    try {
      const { data } = await api.get(`/orders/track`, { params: { ref: ref.trim(), phone: phone.trim() } });
      setResult(data);
    } catch {
      setNotFound(true);
    } finally { setLoading(false); }
  };

  const waHref = `https://wa.me/${wa}?text=${encodeURIComponent(`Hi Wonder Parcel! I need help with my order ${ref || ""}`)}`;

  return (
    <div className="wp-container py-10 sm:py-14">
      <SectionHeading eyebrow="Order tracking" title="Track your parcel" subtitle="Enter your order reference and phone number to see where your gift is." align="center" />

      <div className="mx-auto mt-8 max-w-md">
        <form onSubmit={track} className="space-y-4 rounded-wp-xl border border-border bg-card p-6 shadow-wp-card" data-testid="track-form">
          <div>
            <Label className="mb-1.5 block text-sm">Order reference ID</Label>
            <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. 96bdc227" className="rounded-wp-md" data-testid="track-ref-input" />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Phone number</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone used at checkout" className="rounded-wp-md" data-testid="track-phone-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-xl bg-wp-rose-strong py-5 text-white hover:brightness-105" data-testid="track-submit">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Track my order
          </Button>
        </form>

        {notFound && (
          <div className="mt-4 rounded-wp-lg border border-wp-blush-200 bg-wp-blush-50 p-4 text-center text-sm text-wp-ink" data-testid="track-not-found">
            We couldn't find an order with those details. Please double-check your reference ID and phone number.
            <a href={waHref} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-1.5 font-medium text-wp-success"><MessageCircle className="h-4 w-4" /> Chat with us on WhatsApp</a>
          </div>
        )}

        {result && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-wp-xl border border-border bg-card p-6 shadow-wp-card" data-testid="track-result">
            <div className="flex items-center justify-between">
              <div>
                <p className="wp-label text-[10px] text-wp-ink-muted">Order #{result.ref}</p>
                <p className="font-display text-xl">{result.cancelled ? "Order cancelled" : result.stage_label}</p>
              </div>
              <span className="grid h-11 w-11 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong"><Package className="h-5 w-5" /></span>
            </div>

            {!result.cancelled && result.estimated_delivery && (
              <div className="mt-3 flex items-center gap-2 rounded-wp-md bg-wp-mint/15 px-3 py-2 text-sm text-wp-ocean">
                <Clock className="h-4 w-4" /> Estimated delivery by <span className="font-semibold">{result.estimated_delivery}</span>
              </div>
            )}

            {!result.cancelled && (
              <ol className="mt-5 space-y-0" data-testid="track-timeline">
                {result.stages.map((s, i) => (
                  <li key={s.label} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      {s.done ? <CheckCircle2 className="h-5 w-5 text-wp-success" /> : <Circle className={`h-5 w-5 ${s.current ? "text-wp-rose-strong" : "text-wp-ink-muted/40"}`} />}
                      {i < result.stages.length - 1 && <span className={`my-0.5 w-0.5 flex-1 ${s.done ? "bg-wp-success/40" : "bg-border"}`} style={{ minHeight: 22 }} />}
                    </div>
                    <div className="pb-4">
                      <p className={`text-sm ${s.current ? "font-semibold text-wp-ink" : s.done ? "text-wp-ink" : "text-wp-ink-muted"}`}>{s.label}</p>
                      {s.current && <p className="text-xs text-wp-rose-strong">In progress</p>}
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {result.personalized && result.gift_url && (
              <a href={result.gift_url} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-1.5 rounded-wp-md border border-wp-blush-200 bg-wp-blush-50 py-2.5 text-sm font-medium text-wp-rose-strong" data-testid="track-gift-link">
                <Gift className="h-4 w-4" /> View your personal gift page
              </a>
            )}

            <a href={waHref} target="_blank" rel="noreferrer" className="mt-3 flex items-center justify-center gap-1.5 rounded-xl bg-wp-success/10 py-2.5 text-sm font-semibold text-wp-success" data-testid="track-whatsapp">
              <MessageCircle className="h-4 w-4" /> Need help? Chat on WhatsApp
            </a>
          </motion.div>
        )}
      </div>
    </div>
  );
}
